import React from "react";
import { Link, useParams, Navigate } from "react-router-dom";
import { ArrowLeft, ArrowRight, Quote } from "lucide-react";
import Nav from "@/components/Nav";
import Footer from "@/components/Footer";
import SEO from "@/components/SEO";
import { findCaseStudy, useContentLibrary } from "@/data/content";

const LoadingArticle = () => (
  <div className="bg-white text-zinc-950 dark:bg-zinc-950 dark:text-zinc-100">
    <Nav />
    <main className="pt-32 md:pt-40 pb-24">
      <div className="mx-auto max-w-4xl px-6 md:px-10 text-zinc-600 dark:text-zinc-400">Loading case study...</div>
    </main>
    <Footer />
  </div>
);

export default function CaseStudyDetail() {
  const { slug } = useParams();
  const { data, loading } = useContentLibrary();
  const c = findCaseStudy(data, slug);

  if (loading) return <LoadingArticle />;
  if (!c) return <Navigate to="/case-studies" replace />;

  return (
    <div className="bg-white text-zinc-950 dark:bg-zinc-950 dark:text-zinc-100">
      <SEO
        title={c.title}
        description={c.summary}
        path={`/case-studies/${c.slug}`}
        type="article"
      />
      <Nav />

      <article className="pt-32 md:pt-40 pb-24">
        <div className="mx-auto max-w-4xl px-6 md:px-10">
          <Link
            to="/case-studies"
            data-testid="back-to-case-studies"
            className="inline-flex items-center gap-2 text-sm text-zinc-600 hover:text-[#002FA7] dark:text-zinc-400 dark:hover:text-[#7C9BFF]"
          >
            <ArrowLeft size={14} /> All case studies
          </Link>

          <div className="mt-8 flex flex-wrap items-center gap-3 text-[11px] font-mono uppercase tracking-widest text-zinc-500 dark:text-zinc-400">
            <span className="px-2 py-1 border border-zinc-200 dark:border-zinc-800">{c.industry}</span>
            <span>{c.company}</span>
          </div>

          <h1 className="mt-6 font-display font-black tracking-tighter text-4xl sm:text-5xl lg:text-6xl leading-[0.98] text-zinc-950 dark:text-zinc-50">
            {c.title}
          </h1>

          <div className="mt-10 grid grid-cols-3 gap-3 border-y border-zinc-200 dark:border-zinc-800 py-6">
            {c.metrics.map((m) => (
              <div key={m.v}>
                <div className="font-display font-black tracking-tighter text-2xl sm:text-3xl text-zinc-950 dark:text-zinc-50">{m.k}</div>
                <div className="mt-1 text-[10px] font-mono uppercase tracking-widest text-zinc-500 dark:text-zinc-400">{m.v}</div>
              </div>
            ))}
          </div>

          <Section title="The challenge" body={c.challenge} />
          <Section title="The solution" body={c.solution} />
          <Section title="The outcome" body={c.outcome} />

          <figure className="mt-14 border-l-2 border-[#002FA7] dark:border-[#7C9BFF] pl-6">
            <Quote size={22} className="text-[#002FA7] dark:text-[#7C9BFF]" />
            <blockquote className="mt-3 font-display font-semibold tracking-tight text-2xl sm:text-3xl leading-snug text-zinc-950 dark:text-zinc-50">
              &ldquo;{c.quote.text}&rdquo;
            </blockquote>
            <figcaption className="mt-4 text-sm text-zinc-500 dark:text-zinc-400 font-mono uppercase tracking-widest">
              - {c.quote.author}
            </figcaption>
          </figure>

          <div className="mt-16 bg-zinc-950 text-white p-8 md:p-10 rounded-sm flex flex-col sm:flex-row items-start sm:items-center justify-between gap-5">
            <div>
              <div className="font-display font-bold tracking-tight text-xl">Run a similar program at your company.</div>
              <p className="text-sm text-zinc-300 mt-1">30-minute discovery call. No slideware.</p>
            </div>
            <Link
              to="/#contact"
              className="bg-white text-zinc-950 hover:bg-zinc-200 px-5 py-3 rounded-sm text-sm font-semibold inline-flex items-center gap-2"
            >
              Talk to us <ArrowRight size={15} />
            </Link>
          </div>
        </div>
      </article>

      <Footer />
    </div>
  );
}

const Section = ({ title, body }) => (
  <section className="mt-12">
    <span className="text-xs font-bold uppercase tracking-[0.22em] text-[#002FA7] dark:text-[#7C9BFF]">{title}</span>
    <p className="mt-4 text-lg text-zinc-700 dark:text-zinc-300 leading-relaxed">{body}</p>
  </section>
);
